import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
  },
  title: {
    padding: "14px 16px",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  scroll: { overflowX: "auto" },
  head: { fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
  cell: { fontSize: 14, whiteSpace: "nowrap" },
  empty: { padding: "28px 16px", textAlign: "center", fontSize: 14, color: theme.palette.text.secondary },
}));

/**
 * Tabla de informe (doc 0447, D4). `columns`: [{ key, label, align, render, sort }].
 * Se ordena por la columna pulsada; por defecto, la primera numérica de `defaultSort`.
 */
const ReportTable = ({ title, columns, rows, defaultSort, rowKey = "id" }) => {
  const classes = useStyles();
  const [sort, setSort] = useState({ key: defaultSort, dir: "desc" });

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const value = col?.sort || ((r) => r[sort.key]);
    return [...rows].sort((a, b) => {
      const va = value(a) ?? -Infinity;
      const vb = value(b) ?? -Infinity;
      if (va === vb) return 0;
      const cmp = va > vb ? 1 : -1;
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const toggle = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <section className={classes.card} aria-label={title}>
      {title && <div className={classes.title}>{title}</div>}
      {rows.length === 0 ? (
        <div className={classes.empty}>{i18n.t("reports.empty")}</div>
      ) : (
        <div className={classes.scroll}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align || "left"} className={classes.head}>
                    <TableSortLabel
                      active={sort.key === c.key}
                      direction={sort.key === c.key ? sort.dir : "desc"}
                      onClick={() => toggle(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((r, i) => (
                <TableRow key={r[rowKey] ?? `fila-${i}`} hover>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || "left"} className={classes.cell}>
                      {c.render ? c.render(r) : r[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
};

export default ReportTable;
